import {
  PriceUpliftConfigFormType,
  PriceUpliftConfigResType,
  PriceUpliftConfigTypeEnum,
} from '@monetize/types/app';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { QuoteUpliftPlanStep } from '../utils/quoteRenewalUplift.utils';
import { apiDelete, apiGet, apiPost, apiPut } from './axios';
import { cpqServiceQueryKeys } from './cpqService';
import { priceUpliftServiceQueryKeys } from './queryKeysService';
import { enqueueForQuote } from './quoteWriteChain';

/**
 * Endpoint wrappers shared by the per-offering mutation hooks and the
 * quote-wide plan runner, so the routes live in exactly one place.
 */
export const doCreatePriceUpliftConfig = async (
  quoteId: string,
  quoteOfferingId: string,
  priceUpliftConfig: PriceUpliftConfigFormType,
) => {
  const { data } = await apiPost(
    `/api/quotes/${quoteId}/quoteOfferings/${quoteOfferingId}/priceUplift`,
    priceUpliftConfig,
  );
  return data;
};

export const doUpdatePriceUpliftConfig = async (
  priceUpliftConfigurationId: string,
  priceUpliftConfig: PriceUpliftConfigFormType,
) => {
  const { data } = await apiPut(
    `/api/priceUplift/${priceUpliftConfigurationId}`,
    priceUpliftConfig,
  );
  return data;
};

export const doDeletePriceUpliftConfig = async (
  quoteId: string,
  quoteOfferingId: string,
) => {
  const { data } = await apiDelete(
    `/api/quotes/${quoteId}/quoteOfferings/${quoteOfferingId}/priceUplift`,
  );
  return data;
};

export const useGetPriceUpliftConfig = (
  priceUpliftConfigurationId = '',
  options: Partial<
    UseQueryOptions<PriceUpliftConfigResType, unknown, PriceUpliftConfigResType>
  > = {},
) => {
  return useQuery({
    queryKey: [
      ...priceUpliftServiceQueryKeys.priceUplift(priceUpliftConfigurationId),
    ],
    queryFn: async () => {
      const { data } = await apiGet(
        `/api/priceUplift/${priceUpliftConfigurationId}`,
      );
      return data;
    },
    select: (data) => {
      if (data.type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS) {
        return data;
      }
      const [firstItem] = data.productConfigurations ?? [];
      return {
        ...data,
        percentageType: firstItem?.percentageType,
        amount: null,
      };
    },
    ...options,
  });
};

export const useCreatePriceUpliftConfig = (
  options: Partial<
    UseMutationOptions<
      PriceUpliftConfigResType,
      unknown,
      {
        quoteId: string;
        quoteOfferingId: string;
        priceUpliftConfig: PriceUpliftConfigFormType;
      }
    >
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, quoteOfferingId, priceUpliftConfig }) =>
      enqueueForQuote(quoteId, () =>
        doCreatePriceUpliftConfig(quoteId, quoteOfferingId, priceUpliftConfig),
      ),

    onSuccess: (_, { quoteId }) => {
      queryClient.invalidateQueries({
        queryKey: [...cpqServiceQueryKeys.quotesById(quoteId)],
      });
    },
    ...options,
  });
};

export const useUpdatePriceUpliftConfig = (
  options: Partial<
    UseMutationOptions<
      PriceUpliftConfigResType,
      unknown,
      {
        /**
         * Only used to serialize with the quote's other writes: the endpoint
         * is keyed by configuration id, but the backend re-evaluates the quote
         * on every uplift mutation.
         */
        quoteId: string;
        priceUpliftConfigurationId: string;
        priceUpliftConfig: PriceUpliftConfigFormType;
      }
    >
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, priceUpliftConfigurationId, priceUpliftConfig }) =>
      enqueueForQuote(quoteId, () =>
        doUpdatePriceUpliftConfig(
          priceUpliftConfigurationId,
          priceUpliftConfig,
        ),
      ),
    onSuccess: (data, { priceUpliftConfigurationId }) => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: [
            ...priceUpliftServiceQueryKeys.priceUplift(
              priceUpliftConfigurationId,
            ),
          ],
        });
      }
    },
    ...options,
  });
};

/**
 * Applies a quote-wide renewal uplift plan built by `buildUpliftSavePlan`.
 *
 * There is no bulk endpoint — each offering owns its own configuration — so the
 * steps run one at a time on the quote write chain because the backend
 * re-evaluates the quote on every mutation. The first failure aborts the
 * remaining steps; the caller re-derives from a fresh quote and retries, which
 * the diff engine makes idempotent.
 */
export const useSaveQuoteUpliftPlan = (
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { quoteId: string; steps: QuoteUpliftPlanStep[] }
    >
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ quoteId, steps }) => {
      for (const step of steps) {
        switch (step.action) {
          case 'create':
            await enqueueForQuote(quoteId, () =>
              doCreatePriceUpliftConfig(
                quoteId,
                step.quoteOfferingId,
                step.payload,
              ),
            );
            break;
          case 'update':
            await enqueueForQuote(quoteId, () =>
              doUpdatePriceUpliftConfig(
                step.priceUpliftConfigurationId,
                step.payload,
              ),
            );
            break;
          case 'delete':
            await enqueueForQuote(quoteId, () =>
              doDeletePriceUpliftConfig(quoteId, step.quoteOfferingId),
            );
            break;
        }
      }
    },
    ...options,
    // Partial application on failure still changes server state, so invalidate
    // regardless of the outcome — spread ahead of this so a caller's own
    // `onSettled` composes with the invalidation instead of replacing it.
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...cpqServiceQueryKeys.quotesById(variables.quoteId)],
      });
      queryClient.invalidateQueries({
        queryKey: [...priceUpliftServiceQueryKeys.base],
      });
      options.onSettled?.(data, error, variables, context);
    },
  });
};

export const useDeletePriceUpliftConfig = (
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        quoteId: string;
        quoteOfferingId: string;
        priceUpliftConfigurationId?: string;
      }
    >
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, quoteOfferingId }) =>
      enqueueForQuote(quoteId, () =>
        doDeletePriceUpliftConfig(quoteId, quoteOfferingId),
      ),
    onSuccess: (_, { quoteId }) => {
      queryClient.invalidateQueries({
        queryKey: [...cpqServiceQueryKeys.quotesById(quoteId)],
      });
    },
    ...options,
  });
};
